import api from '../config/api';
import { viatorSearchUrl } from '../utils/affiliateLinks';
import duffelService from './duffelService';
import viatorService from './viator';

/**
 * Unified Travel Service
 * Provides a common interface for all travel providers (Duffel, Viator, etc.)
 * Used across Concierge, Plan, and TopRated sections
 */
class TravelService {
  /**
   * Convert city name to IATA code for API calls
   * @param {string} cityName - City name
   * @returns {string} IATA code or original city name if not found
   */
  async getIATACode(cityName) {
    // Use hardcoded mapping to avoid CORS issues with Google Places API
    const cityToIATA = {
      'dallas': 'DFW',
      'houston': 'IAH',
      'austin': 'AUS',
      'san antonio': 'SAT',
      'miami': 'MIA',
      'los angeles': 'LAX',
      'san francisco': 'SFO',
      'chicago': 'ORD',
      'boston': 'BOS',
      'seattle': 'SEA',
      'new york': 'JFK',
      'london': 'LHR',
      'paris': 'CDG',
      'tokyo': 'NRT',
      'sydney': 'SYD',
      'rome': 'FCO',
      'barcelona': 'BCN',
      'amsterdam': 'AMS',
      'berlin': 'TXL',
      'dubai': 'DXB',
      'singapore': 'SIN',
      'bangkok': 'BKK',
      'las vegas': 'LAS',
      'madrid': 'MAD',
      'munich': 'MUC',
      'frankfurt': 'FRA',
      'milan': 'MXP',
      'milan, metropolitan city of milan, italy': 'MXP',
      'milan, italy': 'MXP',
      'milan metropolitan city of milan italy': 'MXP',
      'zurich': 'ZUR',
      'vienna': 'VIE',
      'copenhagen': 'CPH',
      'stockholm': 'ARN',
      'oslo': 'OSL',
      'cancun': 'CUN',
      'bali': 'DPS',
      'nepal': 'KTM',
      'kathmandu': 'KTM',
      'new zealand': 'AKL',
      'auckland': 'AKL',
      'maldives': 'MLE',
      'male': 'MLE'
    };
    
    // Try exact match first
    const lowerCityName = cityName.toLowerCase();
    if (cityToIATA[lowerCityName]) {
      return cityToIATA[lowerCityName];
    }
    
    // Try partial match
    for (const [key, value] of Object.entries(cityToIATA)) {
      if (lowerCityName.includes(key) || key.includes(lowerCityName)) {
        return value;
      }
    }
    
    return cityName;
  }

  /**
   * Convert city name to Viator destination ID
   * Note: The backend Viator API will handle the conversion using the /destinations endpoint
   * @param {string} cityName - City name
   * @returns {string} Original city name (backend will convert to proper destination ID)
   */
  getViatorDestinationId(cityName) {
    // The backend will use the Viator /destinations API to convert city names to proper destination IDs
    // So we just return the original city name and let the backend handle the conversion
    return cityName;
  }

  /**
   * Get flight recommendations from Duffel (priority source)
   * @param {Object} params - Search parameters
   * @param {string} params.origin - Origin airport code
   * @param {string} params.destination - Destination airport code
   * @param {string} params.departure_date - Departure date (YYYY-MM-DD)
   * @param {string} params.return_date - Return date (YYYY-MM-DD, optional)
   * @param {number} params.passengers - Number of passengers
   * @param {string} params.cabin_class - Cabin class
   * @returns {Promise<Array>} Flight recommendations (Duffel prioritized)
   */
  async getFlightRecommendations(params) {
    try {
      // Convert city names to IATA codes if needed
      let originIATA = params.origin;
      let destinationIATA = params.destination;

      // Check if origin needs conversion (if it's not already a 3-letter code)
      if (params.origin && params.origin.length !== 3) {
        const originCode = await this.getIATACode(params.origin.toLowerCase());
        if (originCode) {
          originIATA = originCode;
        }
      }

      // Check if destination needs conversion (if it's not already a 3-letter code)
      if (params.destination && params.destination.length !== 3) {
        const destCode = await this.getIATACode(params.destination.toLowerCase());
        if (destCode) {
          destinationIATA = destCode;
        }
      }

      // Use converted IATA codes for Duffel search (priority)
      console.log('✈️ Fetching flights from Duffel (priority):', { originIATA, destinationIATA });
      const duffelParams = {
        ...params,
        origin: originIATA,
        destination: destinationIATA
      };

      const response = await duffelService.searchFlights(duffelParams);
      const flights = (response.flights || []).map(flight => ({
        ...flight,
        source: 'duffel',
        priority: 1,
        bookable: true
      }));
      
      console.log(`✅ Duffel returned ${flights.length} flights (priority)`);
      return flights;
    } catch (error) {
      console.warn('❌ Duffel flight search failed:', error);
      return [];
    }
  }

  /**
   * Get hotel recommendations from Duffel (priority source)
   * @param {Object} params - Search parameters
   * @param {string} params.location - Location name
   * @param {string} params.check_in - Check-in date (YYYY-MM-DD)
   * @param {string} params.check_out - Check-out date (YYYY-MM-DD)
   * @param {number} params.guests - Number of guests
   * @returns {Promise<Array>} Hotel recommendations (Duffel prioritized)
   */
  async getHotelRecommendations(params) {
    // Duffel hotel search disabled - returns 0 results and wastes API calls.
    // Hotels are handled via Booking.com fallback in TopRatedSection instead.
    console.log('🏨 Duffel hotel search disabled, skipping for:', params.location);
    return [];
  }

  /**
   * Search stays across vacation rental and hotel providers
   * @param {Object} params - Search parameters
   * @param {string} params.location - Destination name
   * @param {string} params.check_in - Check-in date (YYYY-MM-DD)
   * @param {string} params.check_out - Check-out date (YYYY-MM-DD)
   * @param {number} params.guests - Number of guests
   * @param {string} [params.providers] - Optional comma-separated provider ids
   * @returns {Promise<Object>} Grouped and flat stay listings
   */
  async searchStays(params) {
    try {
      const response = await api.get('/stays/search', {
        params: {
          location: params.location,
          check_in: params.check_in,
          check_out: params.check_out,
          guests: params.guests,
          radius_miles: params.radius_miles,
          providers: params.providers,
          refresh: params.refresh ? 'true' : undefined,
        },
        timeout: 90000,
      });

      return {
        groups: response.data.groups || {},
        results: response.data.results || [],
        query: response.data.query || params,
        providers: response.data.providers || [],
      };
    } catch (error) {
      console.warn('Stays search failed:', error);
      return {
        groups: {},
        results: [],
        query: params,
        providers: [],
      };
    }
  }

  /**
   * Stream stays search results as each provider completes (SSE).
   */
  async searchStaysStream(params, { onStart, onProvider, onDone, onError, signal } = {}) {
    const query = new URLSearchParams({
      location: params.location,
      check_in: params.check_in,
      check_out: params.check_out,
      guests: String(params.guests),
    });

    if (params.radius_miles != null) {
      query.set('radius_miles', String(params.radius_miles));
    }
    if (params.latitude != null && params.longitude != null) {
      query.set('latitude', String(params.latitude));
      query.set('longitude', String(params.longitude));
    }
    if (params.providers) {
      query.set('providers', params.providers);
    }
    if (params.refresh) {
      query.set('refresh', 'true');
    }

    const token = localStorage.getItem('partnerToken') || localStorage.getItem('token');
    const headers = {
      Accept: 'text/event-stream',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${api.defaults.baseURL}/stays/search/stream?${query.toString()}`, {
      method: 'GET',
      headers,
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      const error = new Error(`Stays stream failed (${response.status})`);
      if (onError) {
        onError(error);
      }
      throw error;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      const error = new Error('Stays stream not supported in this browser');
      if (onError) {
        onError(error);
      }
      throw error;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        events.forEach((eventChunk) => {
          const dataLine = eventChunk
            .split('\n')
            .find((line) => line.startsWith('data: '));
          if (!dataLine) {
            return;
          }

          const payload = JSON.parse(dataLine.slice(6));
          if (payload.type === 'start' && onStart) {
            onStart(payload);
          } else if (payload.type === 'provider' && onProvider) {
            onProvider(payload);
          } else if (payload.type === 'done' && onDone) {
            onDone(payload);
          } else if (payload.type === 'error' && onError) {
            onError(new Error(payload.error || payload.message || 'Stays stream error'));
          }
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }

  /**
   * Get detailed stay offer information
   * @param {string} offerId - Stay offer ID
   * @returns {Promise<Object>} Stay offer details
   */
  async getStayOfferDetails(offerId) {
    try {
      // Get stay offer details
      
      const response = await duffelService.getStayOffer(offerId);
      return response;
    } catch (error) {
      console.warn('Duffel stay offer details failed:', error);
      throw error;
    }
  }

  /**
   * Book a stay
   * @param {string} offerId - Stay offer ID to book
   * @param {Object} guestInfo - Guest information
   * @returns {Promise<Object>} Booking confirmation
   */
  async bookStay(offerId, guestInfo) {
    try {
      // Book stay
      
      const response = await duffelService.createStayBooking(offerId, guestInfo);
      return response;
    } catch (error) {
      console.warn('Duffel stay booking failed:', error);
      throw error;
    }
  }

  /**
   * Cancel a stay booking
   * @param {string} bookingId - Stay booking ID to cancel
   * @returns {Promise<Object>} Cancellation confirmation
   */
  async cancelStayBooking(bookingId) {
    try {
      // Cancel stay booking
      
      const response = await duffelService.cancelStayBooking(bookingId);
      return response;
    } catch (error) {
      console.warn('Duffel stay cancellation failed:', error);
      throw error;
    }
  }

  /**
   * Get activity recommendations from Viator
   * @param {string} destination - Destination city name
   * @param {number} limit - Number of activities to return
   * @returns {Promise<Array>} Activity recommendations
   */
  async getActivityRecommendations(destination, limit = 10) {
    try {
      // The backend will use the Viator /destinations API to convert city names to proper destination IDs
      const response = await viatorService.getRecommendations(destination, limit);
      return response.activities || [];
    } catch (error) {
      console.warn('Viator activity search failed:', error);
      
      // Fallback to sample activities when Viator API is not available
      return this.getFallbackActivities(destination, limit);
    }
  }

  /**
   * Get fallback activities when Viator API is not available
   * Returns an empty array to force using Viator search links
   * @param {string} destination - Destination city name
   * @param {number} limit - Number of activities to return
   * @returns {Array} Empty array (Viator should always work)
   */
  getFallbackActivities(destination, limit = 10) {
    // Don't show hardcoded fallback activities - Viator API should work
    // If it fails, the UI will show a message to search on Viator directly
    console.warn(`Viator API failed for ${destination} - returning empty array`);
    return [];
  }

  /**
   * Get events from PredictHQ
   * @param {Object} params - Search parameters
   * @param {number} params.latitude - Latitude
   * @param {number} params.longitude - Longitude
   * @param {string} params.start - Start date (ISO string)
   * @param {string} params.end - End date (ISO string)
   * @param {string} params.radius - Search radius
   * @returns {Promise<Array>} Event recommendations
   */
  async getEventRecommendations(params) {
    try {
      const response = await api.get('/events/search', { params });
      return response.data?.events || [];
    } catch (error) {
      console.warn('Events search failed:', error);
      return [];
    }
  }

  /**
   * Get comprehensive travel recommendations for a destination
   * Combines flights, hotels, activities, and events
   * @param {Object} params - Search parameters
   * @param {string} params.destination - Destination city name
   * @param {string} params.origin - Origin airport code (optional)
   * @param {string} params.departure_date - Departure date (optional)
   * @param {string} params.return_date - Return date (optional)
   * @param {number} params.passengers - Number of passengers (optional)
   * @param {number} params.latitude - Destination latitude (optional)
   * @param {number} params.longitude - Destination longitude (optional)
   * @param {string} params.start - Event start date (optional)
   * @param {string} params.end - Event end date (optional)
   * @returns {Promise<Object>} Combined travel recommendations
   */
  async getComprehensiveRecommendations(params) {
    const {
      destination,
      origin,
      departure_date,
      return_date,
      passengers = 1,
      latitude,
      longitude,
      start,
      end
    } = params;

    const results = {
      flights: [],
      hotels: [],
      activities: [],
      events: []
    };

    // Get activities (no date dependency)
    if (destination) {
      try {
        results.activities = await this.getActivityRecommendations(destination, 10);
      } catch (error) {
        console.warn('Failed to get activities:', error);
      }
    }

    // Get flights from Duffel (priority source - requires origin and dates)
    if (origin && departure_date) {
      try {
        const flights = await this.getFlightRecommendations({
          origin,
          destination: destination || origin, // Use destination or fallback to origin
          departure_date,
          return_date,
          passengers,
          cabin_class: 'economy'
        });
        // Duffel flights are already sorted by priority
        results.flights = flights;
      } catch (error) {
        console.warn('Failed to get flights from Duffel:', error);
      }
    }

    // Duffel hotel search disabled - returns 0 results and wastes API calls.
    // Hotels are handled via Booking.com fallback instead.
    results.hotels = [];

    // Get events (requires coordinates and dates)
    if (latitude && longitude && start && end) {
      try {
        results.events = await this.getEventRecommendations({
          latitude,
          longitude,
          start,
          end,
          radius: '50km'
        });
      } catch (error) {
        console.warn('Failed to get events:', error);
      }
    }

    return results;
  }

  /**
   * Get destination-specific recommendations (activities only)
   * Used when no dates are available
   * @param {string} destination - Destination city name
   * @param {number} limit - Number of activities to return
   * @returns {Promise<Array>} Activity recommendations
   */
  async getDestinationRecommendations(destination, limit = 10) {
    return this.getActivityRecommendations(destination, limit);
  }

  /**
   * Format city name for API calls
   * Handles IATA code conversion and case normalization
   * @param {string} input - City name or IATA code
   * @returns {string} Formatted city name
   */
  formatCityName(input) {
    if (!input) return '';
    
    // If it's a 3-letter IATA code, convert to city name
    if (input.length === 3 && input === input.toUpperCase()) {
      // Import IATA mapping utility
      const { getCityName } = require('../utils/iataToCity');
      return getCityName(input);
    }
    
    // Return lowercase for API consistency
    return input.toLowerCase();
  }

  /**
   * Get Viator city URL for external booking
   * @param {string} cityName - City name
   * @returns {string} Viator search URL
   */
  getViatorCityUrl(cityName) {
    return viatorSearchUrl(cityName);
  }

  /**
   * Get the destination mapping used by the backend
   * This shows how city names are converted to Viator destination IDs
   * @returns {Object} Destination mapping object
   */
  getDestinationMapping() {
    return {
      'dubai': '77',
      'paris': '75', 
      'london': '7',
      'new york': '687',
      'tokyo': '441',
      'sydney': '73',
      'rome': '55',
      'barcelona': '60',
      'amsterdam': '2',
      'berlin': '3'
    };
  }
}

// Export singleton instance
const travelService = new TravelService();
export default travelService;

// Export individual methods for direct use
export const {
  getFlightRecommendations,
  getHotelRecommendations,
  getActivityRecommendations,
  getEventRecommendations,
  getComprehensiveRecommendations,
  getDestinationRecommendations,
  formatCityName,
  getViatorCityUrl,
  getDestinationMapping
} = travelService;
